import { motion } from "framer-motion";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1593780261803-f444c8488a44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXJrJTIwbW9vZHklMjBwYXJraW5nfGVufDB8fHx8MTc4MTI2MzAyMHww&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center justify-center overflow-hidden pt-24 bg-zyp-cream"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Premium car in cinematic apartment garage"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zyp-cream/90 via-white/85 to-zyp-white" />
        <div className="absolute inset-0 zyp-glow" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zyp-navy/10 bg-white/80 backdrop-blur-md mb-8"
          data-testid="hero-overline"
        >
          <Sparkles size={14} className="text-zyp-highlight" />
          <span className="text-xs tracking-[0.2em] uppercase text-zyp-navy/80">
            The smarter way to shine
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-semibold leading-[1.02] tracking-tighter text-zyp-navy"
          data-testid="hero-headline"
        >
          Car Care, <span className="italic font-light text-zyp-navy/80">reimagined.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-8 text-base sm:text-lg md:text-xl text-zyp-navy/70 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subtitle"
        >
          Subscribe-led, doorstep car &amp; bike care for apartment dwellers in
          Bangalore. Limited-period launch pricing live now.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("#waitlist")}
            className="bg-zyp-highlight hover:bg-zyp-highlight-hover text-white rounded-full px-8 h-14 text-base font-medium group"
          >
            Join the waitlist
            <ArrowRight
              size={18}
              className="ml-1 group-hover:translate-x-1 transition-transform"
            />
          </Button>
          <Button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("#how")}
            variant="outline"
            className="bg-white/70 text-zyp-navy border-zyp-navy/20 hover:bg-white hover:text-zyp-navy rounded-full px-8 h-14 text-base font-medium"
          >
            <Play size={16} className="mr-1" />
            See how it works
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
          data-testid="hero-trust-strip"
        >
          {[
            { k: "Free bike wash", v: "With every monthly plan" },
            { k: "7", v: "Pilot societies" },
            { k: "100%", v: "Photo verified" },
            { k: "Akshay Nagar", v: "Launching first" },
          ].map((s) => (
            <div key={s.v} className="text-center">
              <div className="font-heading text-2xl md:text-3xl font-semibold text-zyp-highlight">
                {s.k}
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zyp-navy/60 mt-1">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
